import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type {
  CreateNotePayload,
  GetEntityNotesParams,
  Note,
  NotesListResponse,
} from './types';

/**
 * :
 * GET /note/v1/notes?entityType=&entityId=&page=&pageSize=&isPrivate=
 * POST /note/v1/notes (CreateNotePayload)
 * PUT /note/v1/notes/{id} ({ content, isPrivate })
 * DELETE /note/v1/notes/{id}
 */
function buildNotesQuery(params: GetEntityNotesParams): string {
  const search = new URLSearchParams();
  search.set('entityType', params.entityType);
  search.set('entityId', params.entityId);
  search.set('page', String(params.page ?? 1));
  search.set('pageSize', String(params.pageSize ?? 10));
  if (params.isPrivate !== undefined) search.set('isPrivate', String(params.isPrivate));
  return search.toString();
}

export const notesApi = createApi({
  reducerPath: 'notesApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['Notes'],
  endpoints: (builder) => ({
    getEntityNotes: builder.query<NotesListResponse, GetEntityNotesParams>({
      query: (params) => ({
        url: `${services.note}/v1/notes?${buildNotesQuery(params)}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, params) => [
        { type: 'Notes', id: `${params.entityType}:${params.entityId}` },
      ],
    }),
    createNote: builder.mutation<Note, CreateNotePayload>({
      query: (body) => ({
        url: `${services.note}/v1/notes`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, body) => [
        {
          type: 'Notes',
          id: `${body.resourceReference.entityType}:${body.resourceReference.entityId}`,
        },
      ],
    }),
    updateNote: builder.mutation<Note, { id: string; content: string; isPrivate?: boolean; entityType: string; entityId: string }>({
      query: ({ id, content, isPrivate }) => ({
        url: `${services.note}/v1/notes/${id}`,
        method: 'PUT',
        body: { content, isPrivate },
      }),
      invalidatesTags: (_result, _error, body) => [
        { type: 'Notes', id: `${body.entityType}:${body.entityId}` },
      ],
    }),
    deleteNote: builder.mutation<string, { id: string; entityType: string; entityId: string }>({
      query: ({ id }) => ({
        url: `${services.note}/v1/notes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, body) => [
        { type: 'Notes', id: `${body.entityType}:${body.entityId}` },
      ],
    }),
  }),
});

export const {
  useGetEntityNotesQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
} = notesApi;
